import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { GameMode, SessionSettings } from "../../../shared/types";
import { MIN_TEAMS, MAX_TEAMS, DEFAULT_TEAMS, defaultTeams } from "../../../shared/teams";
import { MUSIC_THEMES, DEFAULT_THEME_ID, rememberMusic, type MusicTheme } from "../../audio/tracks";
import { BACKGROUNDS, DEFAULT_BG_ID, rememberBackground } from "./backgrounds";
import { hostQuiz, hostTokenFor, ApiError } from "../../api";
import { useGame } from "../../net/gameClient";
import { navigate } from "../../router";
import { Ico } from "../../components/shell";

/** Pre-launch config popup: pick Normal vs Teams, team count/names, answer
    shuffle, and music — then create the session and jump into the lobby.
    Owns the whole launch so it can persist the music choice against the new PIN.
    `onBeforeLaunch` lets a caller (e.g. the builder) save/publish first; return
    false to abort the launch. */
export default function LaunchModal({
  quizId,
  quizTitle,
  onClose,
  onBeforeLaunch,
}: {
  quizId: string;
  quizTitle: string;
  onClose: () => void;
  onBeforeLaunch?: () => Promise<boolean>;
}) {
  const [mode, setMode] = useState<GameMode>("solo");
  const [teams, setTeams] = useState(() => defaultTeams(DEFAULT_TEAMS));
  const [shuffleAnswers, setShuffleAnswers] = useState(false);
  const [hideQuestion, setHideQuestion] = useState(false);
  const [remoteControl, setRemoteControl] = useState(false);
  const [themeId, setThemeId] = useState(DEFAULT_THEME_ID);
  const [bgId, setBgId] = useState(DEFAULT_BG_ID);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Remote-control launch is a two-step popup: configure → pair the phone.
  const [step, setStep] = useState<"setup" | "pair">("setup");
  const [pin, setPin] = useState<string | null>(null);
  const previewRef = useRef<HTMLAudioElement | null>(null);

  // While pairing, this tab connects as the (future) big-screen "display" so it
  // can tell when the host's phone has connected as the controller.
  const pairGame = useGame(
    step === "pair" ? pin : null,
    "host",
    pin ? hostTokenFor(pin) : null,
    { surface: "display" },
  );
  const paired = !!pairGame.snapshot?.remoteConnected;
  const remoteUrl = pin ? `${location.origin}/remote/${pin}#${hostTokenFor(pin) ?? ""}` : "";

  useEffect(() => () => previewRef.current?.pause(), []);

  function preview(t: MusicTheme) {
    const el = previewRef.current ?? (previewRef.current = new Audio());
    if (previewing === t.id) {
      el.pause();
      setPreviewing(null);
      return;
    }
    if (!t.lobby) return;
    el.src = t.lobby;
    el.loop = true;
    el.volume = 0.6;
    el.play().catch(() => {});
    setPreviewing(t.id);
  }

  // Resize the team list, keeping any names the host has already edited.
  const setCount = (n: number) => {
    const next = Math.max(MIN_TEAMS, Math.min(MAX_TEAMS, n));
    setTeams((prev) => defaultTeams(next).map((d, i) => (prev[i] ? { ...d, name: prev[i].name } : d)));
  };
  const rename = (i: number, name: string) =>
    setTeams((prev) => prev.map((t, j) => (j === i ? { ...t, name: name.slice(0, 24) } : t)));

  async function start() {
    if (busy) return;
    setBusy(true);
    setErr("");
    if (onBeforeLaunch) {
      const ok = await onBeforeLaunch();
      if (!ok) {
        setBusy(false);
        return;
      }
    }
    const settings: SessionSettings = {
      shuffleAnswers,
      hideQuestionOnPlayer: hideQuestion,
      ...(mode === "teams" ? { mode, teams } : {}),
    };
    try {
      const pin = await hostQuiz(quizId, settings);
      rememberMusic(pin, themeId);
      rememberBackground(pin, bgId);
      previewRef.current?.pause();
      navigate(`/host/${pin}`);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof ApiError ? e.message : "Could not start the session");
    }
  }

  // Remote mode: create the session up front (so there's a PIN to pair against),
  // then switch to the pair screen and wait for the phone to connect.
  async function connectRemote() {
    if (busy) return;
    setBusy(true);
    setErr("");
    if (onBeforeLaunch) {
      const ok = await onBeforeLaunch();
      if (!ok) {
        setBusy(false);
        return;
      }
    }
    const settings: SessionSettings = {
      shuffleAnswers,
      hideQuestionOnPlayer: hideQuestion,
      remoteControl: true,
      ...(mode === "teams" ? { mode, teams } : {}),
    };
    try {
      const newPin = await hostQuiz(quizId, settings);
      rememberMusic(newPin, themeId);
      rememberBackground(newPin, bgId);
      setPin(newPin);
      setStep("pair");
      setBusy(false);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof ApiError ? e.message : "Could not connect remote control");
    }
  }

  // The phone is paired — launch the clean big screen. This click is our one
  // gesture on this device, so prime fullscreen now (and, via sticky activation,
  // the music once HostGame mounts — the SPA document survives the navigation).
  function launchRemote() {
    if (!pin || !paired) return;
    previewRef.current?.pause();
    try {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } catch {
      /* fullscreen unsupported — the host can still trigger it from the phone */
    }
    navigate(`/host/${pin}?remote=1`);
  }

  return (
    <div
      className="atl"
      onClick={busy ? undefined : onClose}
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "grid", placeItems: "center", background: "rgba(40,25,15,.5)", padding: 24 }}
    >
      <div
        className="atl-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, width: "100%", padding: 28, maxHeight: "90vh", overflowY: "auto", animation: "atl-rise .18s ease both" }}
      >
        {step === "setup" ? (
        <>
        <div className="atl-eyebrow atl-eyebrow--accent">START LIVE QUIZ</div>
        <h2 className="atl-serif" style={{ fontSize: 26, marginTop: 8 }}>{quizTitle}</h2>
        <p style={{ color: "var(--atl-soft)", fontSize: 14, fontWeight: 500, marginTop: 6 }}>
          How should bakers play?
        </p>

        {/* mode choice */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 18 }}>
          <ModeCard active={mode === "solo"} onClick={() => setMode("solo")} icon="🧑‍🍳" title="Normal" sub="Everyone plays solo — one score each." />
          <ModeCard active={mode === "teams"} onClick={() => setMode("teams")} icon="👥" title="Teams" sub="Bakers are split into teams — scores combine." />
        </div>

        {mode === "teams" && (
          <div style={{ marginTop: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span className="atl-eyebrow">NUMBER OF TEAMS</span>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
                <Stepper label="−" disabled={teams.length <= MIN_TEAMS} onClick={() => setCount(teams.length - 1)} />
                <span className="atl-mono" style={{ fontSize: 22, fontWeight: 700, width: 24, textAlign: "center" }}>{teams.length}</span>
                <Stepper label="+" disabled={teams.length >= MAX_TEAMS} onClick={() => setCount(teams.length + 1)} />
              </div>
            </div>
            <p style={{ color: "var(--atl-faint)", fontSize: 12.5, fontWeight: 500, marginTop: 8 }}>
              Bakers are randomly placed into teams as they join, kept even. Rename any below.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              {teams.map((t, i) => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 34, height: 34, borderRadius: 10, background: t.color, display: "grid", placeItems: "center", fontSize: 17, flex: "0 0 auto" }}>
                    {t.emoji}
                  </span>
                  <input className="atl-input" value={t.name} onChange={(e) => rename(i, e.target.value)} placeholder={`Team ${i + 1}`} style={{ textAlign: "left" }} />
                </div>
              ))}
            </div>
          </div>
        )}

        <hr className="atl-rule" style={{ margin: "22px 0 18px" }} />

        {/* answer shuffle */}
        <button
          type="button"
          onClick={() => setShuffleAnswers((v) => !v)}
          aria-pressed={shuffleAnswers}
          className="atl-card"
          style={{
            display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 16px",
            cursor: "pointer", textAlign: "left",
            border: `1px solid ${shuffleAnswers ? "var(--atl-accent)" : "var(--atl-line)"}`,
            background: shuffleAnswers ? "#fbf2f3" : "var(--atl-surface)",
          }}
        >
          <span style={{ flex: 1 }}>
            <span style={{ fontWeight: 700, fontSize: 14.5, display: "block" }}>Randomise answer order</span>
            <span style={{ display: "block", color: "var(--atl-soft)", fontWeight: 500, fontSize: 12.5, marginTop: 3 }}>
              Shuffle answer positions each session, so the right answer isn't always in the same spot.
            </span>
          </span>
          <Toggle on={shuffleAnswers} />
        </button>

        <button
          type="button"
          onClick={() => setHideQuestion((v) => !v)}
          aria-pressed={hideQuestion}
          className="atl-card"
          style={{
            display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 16px",
            cursor: "pointer", textAlign: "left", marginTop: 10,
            border: `1px solid ${hideQuestion ? "var(--atl-accent)" : "var(--atl-line)"}`,
            background: hideQuestion ? "#fbf2f3" : "var(--atl-surface)",
          }}
        >
          <span style={{ flex: 1 }}>
            <span style={{ fontWeight: 700, fontSize: 14.5, display: "block" }}>Hide question on phones</span>
            <span style={{ display: "block", color: "var(--atl-soft)", fontWeight: 500, fontSize: 12.5, marginTop: 3 }}>
              Players see only the answer tiles and read the question off the big screen.
            </span>
          </span>
          <Toggle on={hideQuestion} />
        </button>

        {/* remote control */}
        <button
          type="button"
          onClick={() => setRemoteControl((v) => !v)}
          aria-pressed={remoteControl}
          className="atl-card"
          style={{
            display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 16px",
            cursor: "pointer", textAlign: "left", marginTop: 10,
            border: `1px solid ${remoteControl ? "var(--atl-accent)" : "var(--atl-line)"}`,
            background: remoteControl ? "#fbf2f3" : "var(--atl-surface)",
          }}
        >
          <span style={{ fontSize: 20, flex: "0 0 auto" }}>📱</span>
          <span style={{ flex: 1 }}>
            <span style={{ fontWeight: 700, fontSize: 14.5, display: "block" }}>Use remote control</span>
            <span style={{ display: "block", color: "var(--atl-soft)", fontWeight: 500, fontSize: 12.5, marginTop: 3 }}>
              Run the whole quiz from your phone — the big screen stays clean. You'll scan a QR to connect.
            </span>
          </span>
          <Toggle on={remoteControl} />
        </button>

        {/* music */}
        <div style={{ marginTop: 18 }}>
          <div className="atl-eyebrow" style={{ marginBottom: 10 }}>MUSIC</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {MUSIC_THEMES.map((t) => {
              const on = themeId === t.id;
              return (
                <div
                  key={t.id}
                  onClick={() => setThemeId(t.id)}
                  className="atl-card"
                  style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", cursor: "pointer",
                    border: `1px solid ${on ? "var(--atl-accent)" : "var(--atl-line)"}`,
                    background: on ? "#fbf2f3" : "var(--atl-surface)",
                  }}
                >
                  <span style={{ width: 18, height: 18, borderRadius: "50%", flex: "0 0 auto", border: `2px solid ${on ? "var(--atl-accent)" : "var(--atl-line)"}`, background: on ? "var(--atl-accent)" : "transparent", boxShadow: on ? "inset 0 0 0 3px #fff" : "none" }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</span>
                    <span style={{ display: "block", color: "var(--atl-soft)", fontWeight: 500, fontSize: 12 }}>{t.blurb}</span>
                  </span>
                  {t.lobby && (
                    <button type="button" className="atl-btn atl-btn--ghost" style={{ padding: "6px 12px", fontSize: 13 }} onClick={(e) => { e.stopPropagation(); preview(t); }}>
                      {previewing === t.id ? "⏸ Stop" : "▷ Preview"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* big-screen background */}
        <div style={{ marginTop: 18 }}>
          <div className="atl-eyebrow" style={{ marginBottom: 10 }}>BIG-SCREEN BACKGROUND</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 9 }}>
            {BACKGROUNDS.map((b) => {
              const on = bgId === b.id;
              return (
                <button
                  type="button"
                  key={b.id}
                  onClick={() => setBgId(b.id)}
                  aria-pressed={on}
                  title={b.name}
                  style={{
                    display: "flex", flexDirection: "column", gap: 6, padding: 0, cursor: "pointer",
                    background: "none", border: "none", textAlign: "center",
                  }}
                >
                  <span
                    style={{
                      height: 46, borderRadius: 10, display: "block",
                      background: b.css ?? "#FAF4EC",
                      border: `2px solid ${on ? "var(--atl-accent)" : "var(--atl-line)"}`,
                      boxShadow: on ? "0 0 0 3px #f7e7e9" : "inset 0 0 0 1px rgba(0,0,0,.03)",
                    }}
                  />
                  <span style={{ fontSize: 11, fontWeight: on ? 700 : 500, color: on ? "var(--atl-accent)" : "var(--atl-soft)", lineHeight: 1.2 }}>
                    {b.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {err && (
          <div className="atl-chip atl-chip--accent" style={{ marginTop: 16, justifyContent: "center", padding: "9px 12px", width: "100%" }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          <button className="atl-btn atl-btn--ghost" onClick={onClose} disabled={busy} style={{ flex: "0 0 auto" }}>Cancel</button>
          {remoteControl ? (
            <button className="atl-btn atl-btn--primary atl-btn--lg" onClick={connectRemote} disabled={busy} style={{ flex: 1 }}>
              <span style={{ fontSize: 15 }}>📱</span> {busy ? "Connecting…" : "Connect remote control"}
            </button>
          ) : (
            <button className="atl-btn atl-btn--primary atl-btn--lg" onClick={start} disabled={busy} style={{ flex: 1 }}>
              <Ico name="play" size={15} /> {busy ? "Lighting the oven…" : "Start the bake"}
            </button>
          )}
        </div>
        </>
        ) : (
        <>
        {/* ── PAIR SCREEN — scan to pick up the controls on the phone ── */}
        <div className="atl-eyebrow atl-eyebrow--accent">REMOTE CONTROL</div>
        <h2 className="atl-serif" style={{ fontSize: 26, marginTop: 8 }}>Connect your phone</h2>
        <p style={{ color: "var(--atl-soft)", fontSize: 14, fontWeight: 500, marginTop: 6 }}>
          Scan this with your phone to pick up the controls. The big screen stays clean — you'll run
          the whole quiz from your hand.
        </p>

        <div style={{ display: "grid", placeItems: "center", marginTop: 22 }}>
          <div style={{ background: "#fff", padding: 16, borderRadius: 20, boxShadow: "var(--sh)", lineHeight: 0, opacity: paired ? 0.45 : 1, transition: "opacity .3s ease" }}>
            {remoteUrl && <QRCodeSVG value={remoteUrl} size={208} level="M" />}
          </div>
        </div>

        <div
          data-remote-url={remoteUrl}
          className="atl-chip"
          style={{
            marginTop: 20, width: "100%", justifyContent: "center", padding: "11px 14px", gap: 9,
            fontWeight: 700, fontSize: 14,
            background: paired ? "#e9f6ec" : "var(--atl-surface)",
            border: `1px solid ${paired ? "var(--good, #3a9d5d)" : "var(--atl-line)"}`,
            color: paired ? "#1f7a3d" : "var(--atl-soft)",
          }}
        >
          {paired ? (
            <>
              <Ico name="check" size={16} /> Phone connected — ready to bake
            </>
          ) : (
            <>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--atl-accent)", flex: "0 0 auto", animation: "bk-pulse 1.2s ease-in-out infinite" }} />
              Waiting for your phone…
            </>
          )}
        </div>

        {err && (
          <div className="atl-chip atl-chip--accent" style={{ marginTop: 16, justifyContent: "center", padding: "9px 12px", width: "100%" }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          <button className="atl-btn atl-btn--ghost" onClick={() => setStep("setup")} style={{ flex: "0 0 auto" }}>
            Back
          </button>
          <button
            className="atl-btn atl-btn--primary atl-btn--lg"
            onClick={launchRemote}
            disabled={!paired}
            style={{ flex: 1, opacity: paired ? 1 : 0.55 }}
          >
            <Ico name="play" size={15} /> Start the bake
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function ModeCard({ active, onClick, icon, title, sub }: { active: boolean; onClick: () => void; icon: string; title: string; sub: string }) {
  return (
    <button
      onClick={onClick}
      className="atl-card"
      style={{
        textAlign: "left", padding: 16, cursor: "pointer",
        border: active ? "1.5px solid var(--atl-accent)" : "1px solid var(--atl-line)",
        boxShadow: active ? "0 0 0 3px #f7e7e9" : undefined,
        background: active ? "#fbf2f3" : "var(--atl-surface)",
      }}
    >
      <div style={{ fontSize: 24 }}>{icon}</div>
      <div className="atl-serif" style={{ fontSize: 18, marginTop: 8 }}>{title}</div>
      <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--atl-soft)", marginTop: 4, lineHeight: 1.4 }}>{sub}</div>
    </button>
  );
}

function Stepper({ label, onClick, disabled }: { label: string; onClick: () => void; disabled: boolean }) {
  return (
    <button
      className="atl-btn atl-btn--ghost"
      onClick={onClick}
      disabled={disabled}
      style={{ width: 38, height: 38, padding: 0, borderRadius: 10, fontSize: 20, fontWeight: 700 }}
      aria-label={label === "+" ? "More teams" : "Fewer teams"}
    >
      {label}
    </button>
  );
}

/** Tiny pill switch — visual only; the parent owns the state. */
function Toggle({ on }: { on: boolean }) {
  return (
    <span style={{ width: 46, height: 28, borderRadius: 99, flex: "0 0 auto", background: on ? "var(--atl-accent)" : "var(--atl-line)", position: "relative", transition: "background .15s ease" }}>
      <span style={{ position: "absolute", top: 3, left: on ? 21 : 3, width: 22, height: 22, borderRadius: "50%", background: "#fff", transition: "left .15s ease", boxShadow: "0 1px 3px rgba(0,0,0,.25)" }} />
    </span>
  );
}
